import React, { useState, useEffect } from 'react';
import {
    Card,
    Form,
    Input,
    Select,
    Switch,
    Button,
    Row,
    Col,
    Divider,
    Typography,
    Alert,
    message,
    Tabs,
    InputNumber,
    Space,
    Tag,
    Tooltip,
    Modal,
    Table,
    Popconfirm,
} from 'antd';
import {
    SaveOutlined,
    PlayCircleOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    CreditCardOutlined,
    DollarOutlined,
    SettingOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PaymentSettings = () => {
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState({});
    const [form] = Form.useForm();
    const [testModalVisible, setTestModalVisible] = useState(false);
    const [testResults, setTestResults] = useState([]);
    const [showSecrets, setShowSecrets] = useState({});

    const paymentProviders = [
        {
            key: 'razorpay',
            name: 'Razorpay',
            description: 'Primary payment gateway for India (UPI, Cards, Wallets, Net Banking)',
            icon: '💳',
            features: ['UPI', 'Cards', 'Wallets', 'Net Banking', 'EMI'],
            status: 'active'
        },
        {
            key: 'stripe',
            name: 'Stripe',
            description: 'International payment gateway (Cards, Apple Pay, Google Pay)',
            icon: '🌍',
            features: ['Cards', 'Apple Pay', 'Google Pay', 'International'],
            status: 'active'
        },
        {
            key: 'payu',
            name: 'PayU',
            description: 'Alternative payment gateway for India',
            icon: '🏦',
            features: ['UPI', 'Cards', 'Wallets', 'Net Banking'],
            status: 'inactive'
        },
        {
            key: 'cod',
            name: 'Cash on Delivery',
            description: 'Traditional payment method with processing fees',
            icon: '💵',
            features: ['Cash Payment', 'No Online Required'],
            status: 'active'
        }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetIntegrations();
            if (response.success) {
                const paymentIntegrations = response.data.filter(integration => 
                    ['razorpay', 'stripe', 'payu', 'cod'].includes(integration.type)
                );
                
                const settings = {};
                paymentIntegrations.forEach(integration => {
                    settings[integration.type] = {
                        enabled: integration.is_active,
                        config: integration.config || {},
                        test_mode: integration.test_mode || false
                    };
                });
                
                form.setFieldsValue(settings);
            }
        } catch (error) {
            message.error('Failed to load payment settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        setLoading(true);
        try {
            // Save each payment provider configuration
            for (const [provider, config] of Object.entries(values)) {
                if (paymentProviders.find(p => p.key === provider)) {
                    await api.adminUpdateIntegration(provider, {
                        type: provider,
                        name: paymentProviders.find(p => p.key === provider).name,
                        is_active: config.enabled || false,
                        test_mode: config.test_mode || false,
                        config: {
                            key_id: config.key_id || '',
                            key_secret: config.key_secret || '',
                            publishable_key: config.publishable_key || '',
                            webhook_secret: config.webhook_secret || '',
                            merchant_id: config.merchant_id || '',
                            ...config.config || {}
                        }
                    });
                }
            }
            
            message.success('Payment settings saved successfully');
        } catch (error) {
            message.error('Failed to save payment settings');
        } finally {
            setLoading(false);
        }
    };

    const testPaymentProvider = async (provider) => {
        setTesting(prev => ({ ...prev, [provider]: true }));
        try {
            const result = await api.adminTestIntegration(provider);
            setTestResults(prev => [...prev, {
                provider,
                success: result.success,
                message: result.message,
                timestamp: new Date().toLocaleString()
            }]);
            message.success(`${provider} test completed`);
        } catch (error) {
            setTestResults(prev => [...prev, {
                provider,
                success: false,
                message: error.message || 'Test failed',
                timestamp: new Date().toLocaleString()
            }]);
            message.error(`${provider} test failed`);
        } finally {
            setTesting(prev => ({ ...prev, [provider]: false }));
        }
    };

    const toggleSecretVisibility = (provider) => {
        setShowSecrets(prev => ({ ...prev, [provider]: !prev[provider] }));
    };

    const renderProviderConfig = (provider) => {
        const providerInfo = paymentProviders.find(p => p.key === provider);
        if (!providerInfo) return null;

        return (
            <Card
                key={provider}
                title={
                    <Space>
                        <span style={{ fontSize: '20px' }}>{providerInfo.icon}</span>
                        <span>{providerInfo.name}</span>
                        <Tag color={providerInfo.status === 'active' ? 'green' : 'red'}>
                            {providerInfo.status}
                        </Tag>
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            icon={<PlayCircleOutlined />}
                            onClick={() => testPaymentProvider(provider)}
                            loading={testing[provider]}
                        >
                            Test
                        </Button>
                        <Switch
                            checked={form.getFieldValue([provider, 'enabled'])}
                            onChange={(checked) => form.setFieldValue([provider, 'enabled'], checked)}
                        />
                    </Space>
                }
                style={{ marginBottom: 16 }}
            >
                <Paragraph>{providerInfo.description}</Paragraph>
                
                <Row gutter={16}>
                    <Col span={24}>
                        <Space wrap>
                            {providerInfo.features.map(feature => (
                                <Tag key={feature} color="blue">{feature}</Tag>
                            ))}
                        </Space>
                    </Col>
                </Row>

                <Divider />

                {provider === 'razorpay' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'key_id']}
                                label="Key ID"
                                rules={[{ required: true, message: 'Key ID is required' }]}
                            >
                                <Input placeholder="rzp_test_..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'key_secret']}
                                label="Key Secret"
                                rules={[{ required: true, message: 'Key Secret is required' }]}
                            >
                                <Input
                                    type={showSecrets[provider] ? 'text' : 'password'}
                                    placeholder="Enter key secret"
                                    suffix={
                                        <Tooltip title={showSecrets[provider] ? 'Hide' : 'Show'}>
                                            {showSecrets[provider] ? 
                                                <EyeInvisibleOutlined onClick={() => toggleSecretVisibility(provider)} /> :
                                                <EyeOutlined onClick={() => toggleSecretVisibility(provider)} />
                                            }
                                        </Tooltip>
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name={[provider, 'webhook_secret']}
                                label="Webhook Secret"
                            >
                                <Input placeholder="Enter webhook secret" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {provider === 'stripe' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'publishable_key']}
                                label="Publishable Key"
                                rules={[{ required: true, message: 'Publishable Key is required' }]}
                            >
                                <Input placeholder="pk_test_..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'key_secret']}
                                label="Secret Key"
                                rules={[{ required: true, message: 'Secret Key is required' }]}
                            >
                                <Input
                                    type={showSecrets[provider] ? 'text' : 'password'}
                                    placeholder="sk_test_..."
                                    suffix={
                                        <Tooltip title={showSecrets[provider] ? 'Hide' : 'Show'}>
                                            {showSecrets[provider] ? 
                                                <EyeInvisibleOutlined onClick={() => toggleSecretVisibility(provider)} /> :
                                                <EyeOutlined onClick={() => toggleSecretVisibility(provider)} />
                                            }
                                        </Tooltip>
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name={[provider, 'webhook_secret']}
                                label="Webhook Secret"
                            >
                                <Input placeholder="whsec_..." />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {provider === 'payu' && (
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name={[provider, 'merchant_id']}
                                label="Merchant ID"
                            >
                                <Input placeholder="Enter merchant ID" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name={[provider, 'key_id']}
                                label="Key ID"
                            >
                                <Input placeholder="Enter key ID" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name={[provider, 'key_secret']}
                                label="Key Secret"
                            >
                                <Input
                                    type={showSecrets[provider] ? 'text' : 'password'}
                                    placeholder="Enter key secret"
                                    suffix={
                                        <Tooltip title={showSecrets[provider] ? 'Hide' : 'Show'}>
                                            {showSecrets[provider] ? 
                                                <EyeInvisibleOutlined onClick={() => toggleSecretVisibility(provider)} /> :
                                                <EyeOutlined onClick={() => toggleSecretVisibility(provider)} />
                                            }
                                        </Tooltip>
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {provider === 'cod' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'processing_fee']}
                                label="Processing Fee (₹)"
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name={[provider, 'min_order_amount']}
                                label="Minimum Order Amount (₹)"
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name={[provider, 'test_mode']}
                            label="Test Mode"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        );
    };

    const testColumns = [
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
            render: (provider) => (
                <Space>
                    <span style={{ fontSize: '16px' }}>
                        {paymentProviders.find(p => p.key === provider)?.icon}
                    </span>
                    <span>{paymentProviders.find(p => p.key === provider)?.name}</span>
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'success',
            key: 'success',
            render: (success) => (
                <Tag color={success ? 'green' : 'red'} icon={success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {success ? 'Success' : 'Failed'}
                </Tag>
            )
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message'
        },
        {
            title: 'Time',
            dataIndex: 'timestamp',
            key: 'timestamp'
        }
    ];

    return (
        <div>
            <Title level={2}>
                <CreditCardOutlined /> Payment Settings
            </Title>
            <Paragraph>
                Configure payment gateways and manage payment processing settings for your application.
            </Paragraph>

            <Alert
                message="Security Notice"
                description="Payment credentials are encrypted and stored securely. Never share your API keys publicly."
                type="info"
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 24 }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                initialValues={{}}
            >
                <Tabs defaultActiveKey="providers">
                    <Tabs.TabPane tab="Payment Providers" key="providers">
                        {paymentProviders.map(provider => renderProviderConfig(provider.key))}
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="General Settings" key="general">
                        <Card title="Payment Configuration">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="default_currency"
                                        label="Default Currency"
                                        initialValue="INR"
                                    >
                                        <Select>
                                            <Option value="INR">INR (Indian Rupee)</Option>
                                            <Option value="USD">USD (US Dollar)</Option>
                                            <Option value="EUR">EUR (Euro)</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="payment_timeout"
                                        label="Payment Timeout (minutes)"
                                        initialValue={15}
                                    >
                                        <InputNumber min={1} max={60} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="refund_policy_days"
                                        label="Refund Policy (days)"
                                        initialValue={7}
                                    >
                                        <InputNumber min={0} max={30} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="auto_refund_enabled"
                                        label="Enable Auto Refunds"
                                        valuePropName="checked"
                                        initialValue={false}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="Test Results" key="tests">
                        <Card
                            title="Payment Provider Tests"
                            extra={
                            <Button
                                icon={<PlayCircleOutlined />}
                                onClick={() => setTestModalVisible(true)}
                            >
                                Run All Tests
                            </Button>
                            }
                        >
                            <Table
                                columns={testColumns}
                                dataSource={testResults}
                                rowKey="timestamp"
                                pagination={false}
                                size="small"
                            />
                        </Card>
                    </Tabs.TabPane>
                </Tabs>

                <Divider />

                <Space>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        icon={<SaveOutlined />}
                        size="large"
                    >
                        Save All Settings
                    </Button>
                    <Button
                        icon={<PlayCircleOutlined />}
                        onClick={() => setTestModalVisible(true)}
                        size="large"
                    >
                        Test All Providers
                    </Button>
                </Space>
            </Form>

            <Modal
                title="Payment Provider Tests"
                visible={testModalVisible}
                onCancel={() => setTestModalVisible(false)}
                footer={null}
                width={800}
            >
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <PlayCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                    <Title level={4}>Running Payment Tests...</Title>
                    <Paragraph>This will test all enabled payment providers to ensure they are working correctly.</Paragraph>
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                paymentProviders.forEach(provider => {
                                    if (form.getFieldValue([provider.key, 'enabled'])) {
                                        testPaymentProvider(provider.key);
                                    }
                                });
                                setTestModalVisible(false);
                            }}
                        >
                            Start Tests
                        </Button>
                        <Button onClick={() => setTestModalVisible(false)}>
                            Cancel
                        </Button>
                    </Space>
                </div>
            </Modal>
        </div>
    );
};

export default PaymentSettings;
